/* global dataLayer */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import Cookies from 'universal-cookie';

import { withClientConfig, SoyuzExperiment } from '@spotahome/soyuz/client';
import { ToastContainer } from '@spotahome/ui-library';

import {
  getNumberActiveBanners,
  bannerList
} from '@common/components/HomeCardBanner/config';

import { requestSearchData } from '@common/graphql/queries/search';
import { getAreaInfoQuery } from '@common/graphql/queries/areas';

import { MatcherProvider } from '@common/context/MatcherContext';

import {
  InstanceManager,
  CustomDimensionsManager,
  constants
} from '@common/tracking';

import SessionExpiredModal from '@common/components/SessionExpiredModal';
import AlertsLoginModal from '@common/components/AlertsLoginModal';
import FavoriteClaim from '@common/components/FavoriteClaim';
import FavoriteClaimLoginModal from '@common/components/FavoriteClaimLoginModal';
import CookiesBanner from '@common/components/Cookies';
import HeaderContainer from '@common/components/HeaderContainer';
import InformationToastWrapper from '@common/components/InformationToast/index';
import AppBanner from '@common/components/AppBanner';
import ValuePropositionModal from '@common/components/ValuePropositionModal/index';
import ListingUnavailableModal from '@common/components/ListingUnavailableModal';
import isFeatureDateToggleEnabled from '@common/utils/featureDateToggle';

import CurrencyFormatter from '@utils/CurrencyFormatter';

import hideCancellationPoliciesShape from '@sru/shapes/hideCancellationPolicies';

import SahContainer from '../../LEGACY/javascripts/ui/sah-container';
import Search from '../../LEGACY/javascripts/search/components/Search';

import {
  MQ_TABLET_MIN_WIDTH,
  COOKIE_VALUE_PROPOSITION_MODAL
} from '../../common/constants';

import { isSeoMode } from './utils/seo-mode';

import poiShape from './shapes/poi';
import cityShape from './shapes/city';

import { getInitialFilters, adaptFilters } from './utils/filters';
import { buildHeadersData } from './builders/headers';
import { buildAppData } from './builders/app';

import { Provider } from './IsUserLoggedContext';

const cookies = new Cookies();

const getMetadataUrl = (url, city) => {
  const startIndex = url.indexOf(`/${city}`);
  const endIndex = url.length;

  return url.substring(startIndex, endIndex);
};

const getMetadataLocale = locale => (locale === 'en' ? '' : locale);
const SHOULD_SHIFT_MARKERS = true;
const MARKERS_PER_PAGE = 60;

const getSortingAlgorithm = ({ experimentVariant, cityId }) => {
  const defaultAlgorithm =
    cityId === 'berlin' ? 'WeightedParams-celebrimbor' : 'DataScience-model2';

  if (experimentVariant === 'new-algorithm') {
    return 'DataScience-model3';
  }
  return defaultAlgorithm;
};

class SearchPage extends Component {
  static NAME = 'SearchPage';

  static contextTypes = {
    data: PropTypes.shape({
      SearchPage: PropTypes.shape({
        poi: PropTypes.shape(poiShape),
        city: PropTypes.shape(cityShape).isRequired,
        maxPriceInCity: PropTypes.number.isRequired,
        seoLinks: PropTypes.arrayOf(
          PropTypes.shape({
            anchorText: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
          })
        ),
        initialFiltersFromQuery: PropTypes.object.isRequired,
        showUnavailableModal: PropTypes.bool.isRequired,
        showMatcherModal: PropTypes.bool.isRequired
      }).isRequired
    }).isRequired
  };
  static propTypes = {
    clientConfig: PropTypes.shape({
      featureToggle: PropTypes.shape({
        showHomecardBanner: PropTypes.bool,
        hideCancellationPolicies: PropTypes.bool,
        ...hideCancellationPoliciesShape
      })
    }).isRequired
  };

  static async requestAllData(req, _, { clientConfig, graphqlClient }) {
    const { params, originalUrl, locale, userId } = req;
    const { city, area } = params;

    try {
      let areaInfo;
      if (area) {
        const areaResponse = await getAreaInfoQuery(graphqlClient)(city, area);
        areaInfo = areaResponse.data.areaInfo;
      }

      const initialFiltersFromQuery = getInitialFilters(originalUrl, areaInfo);

      const experiment = req.getExperiment('SearchAlgorithm');
      const searchAlgorithm = getSortingAlgorithm({
        experimentVariant: experiment.get('Algorithm-version'),
        cityId: city
      });

      const adaptedFilters = adaptFilters(
        initialFiltersFromQuery,
        searchAlgorithm
      );

      const limit = clientConfig.featureToggle.showHomecardBanner
        ? MARKERS_PER_PAGE -
          getNumberActiveBanners(
            bannerList(
              city.id,
              isFeatureDateToggleEnabled(
                clientConfig.featureToggle.hideCancellationPolicies
              )
            )
          )
        : MARKERS_PER_PAGE;

      const searchResponse = await requestSearchData(
        graphqlClient,
        clientConfig.featureToggle
      )(
        city,
        adaptedFilters,
        getMetadataLocale(locale),
        initialFiltersFromQuery['move-in'],
        initialFiltersFromQuery['move-out'],
        getMetadataUrl(originalUrl, city),
        limit
      );

      if (searchResponse.errors && searchResponse.errors.length) {
        searchResponse.errors.forEach(err => {
          req.logger.error('Search Page SSR GraphQL', { err });
        });
      }

      const { isLogged, metadata, ...searchData } = searchResponse.data;
      const { paginator } = searchData.search;
      const serverTrackingInstance = InstanceManager.getServerSideInstance();
      const cdManager = CustomDimensionsManager(serverTrackingInstance);

      cdManager.setSearchQueryCustomDimensions(
        city,
        adaptedFilters,
        paginator.count
      );

      cdManager.setSearchCommonCustomDimensions({
        city,
        moveIn: initialFiltersFromQuery['move-in'],
        moveOut: initialFiltersFromQuery['move-out'],
        locale,
        isLogged,
        pageType: constants.TRACKING_PAGE_TYPES.SEARCH_PAGE
      });

      serverTrackingInstance.sendEvent({
        eventCategory: 'Search',
        eventAction: 'user search',
        eventLabel: 'initial load'
      });

      const dataLayerScript = serverTrackingInstance.flush();

      const showUnavailableModal = req.query['show-modal'] === 'unavailable';
      const showMatcherModal = req.query.homematcher === 'true';

      return {
        headers: buildHeadersData(
          city,
          clientConfig,
          metadata,
          initialFiltersFromQuery,
          paginator,
          req,
          dataLayerScript
        ),
        data: buildAppData({
          searchData: { isLogged, ...searchData, metadata },
          initialFiltersFromQuery,
          SHOULD_SHIFT_MARKERS,
          userId,
          showUnavailableModal,
          showMatcherModal
        })
      };
    } catch (error) {
      console.error(error);
    }

    return {
      headers: {},
      body: {}
    };
  }

  state = {
    ...this.context.data[SearchPage.NAME]
  };

  componentDidMount() {
    const { userId } = this.state;
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({
        userId
      });
    }
  }

  render() {
    const {
      city,
      allCities,
      poi,
      maxPriceInCity,
      initialFiltersFromQuery,
      initialMarkerList = [],
      initialHomecards = {},
      initialUnitPrices,
      isUserLogged,
      numberOfMarkers,
      areas,
      metadata,
      showUnavailableModal,
      showMatcherModal
    } = this.state;
    const {
      clientConfig: {
        featureToggle: { hideCancellationPolicies, showHomecardBanner }
      }
    } = this.props;

    const countryCode = city.country.countryIsoCode;
    const cities = city.allCities;
    const otherCitiesInCountry = city.otherCitiesInCountry;
    const currency = new CurrencyFormatter(city.currencyIsoCode);
    const IS_SEO_MODE = isSeoMode(initialFiltersFromQuery);

    return (
      <SahContainer>
        <AppBanner source="search" />
        <HeaderContainer />
        <ToastContainer />
        <SoyuzExperiment name="SearchVPModal2">
          {experiment =>
            experiment.get('behaviour') === 'variant' &&
            cookies.get(COOKIE_VALUE_PROPOSITION_MODAL) === undefined && (
              <ValuePropositionModal />
            )
          }
        </SoyuzExperiment>
        {showUnavailableModal && <ListingUnavailableModal />}
        <InformationToastWrapper city={this.state.city.id} />
        <Provider
          value={{
            isUserLogged,
            allCities
          }}
        >
          <MatcherProvider
            showMatcherModal={showMatcherModal}
            maxPriceInCity={maxPriceInCity}
            currency={currency}
            initialUnitPrices={initialUnitPrices}
            cityId={city.id}
            minimumStaying={city.minimumStaying}
          >
            <SoyuzExperiment name="NoPriceTillDates">
              {experiment => {
                const hidePriceWithoutDates =
                  experiment.get('behaviour') === 'hide';
                return (
                  <SoyuzExperiment name="SearchAlgorithm">
                    {searchAlgorithmExperiment => (
                      <Search
                        discounts={city.discounts}
                        sessionPoi={poi}
                        city={city}
                        countryCode={countryCode}
                        currency={currency}
                        maxPriceInCity={maxPriceInCity}
                        initialFiltersFromQuery={initialFiltersFromQuery}
                        seoMode={IS_SEO_MODE}
                        seoContent={metadata.content}
                        allCities={cities}
                        otherCitiesInCountry={otherCitiesInCountry}
                        initialMarkerList={initialMarkerList}
                        initialHomecards={initialHomecards}
                        initialUnitPrices={initialUnitPrices}
                        numberOfMarkers={numberOfMarkers}
                        sortingAlgorithm={getSortingAlgorithm({
                          experimentVariant: searchAlgorithmExperiment.get(
                            'Algorithm-version'
                          ),
                          cityId: this.state.city.id
                        })}
                        itemsPerPage={
                          showHomecardBanner
                            ? MARKERS_PER_PAGE -
                              getNumberActiveBanners(
                                bannerList(
                                  city.id,
                                  isFeatureDateToggleEnabled(
                                    hideCancellationPolicies
                                  )
                                )
                              )
                            : MARKERS_PER_PAGE
                        }
                        areas={areas}
                        hidePriceWithoutDates={hidePriceWithoutDates}
                      />
                    )}
                  </SoyuzExperiment>
                );
              }}
            </SoyuzExperiment>
          </MatcherProvider>
        </Provider>
        <SessionExpiredModal />
        <AlertsLoginModal />
        <SoyuzExperiment name="PromoteSignupInFavoritesNamespace">
          {ns =>
            ns.get('behaviour') === 'original' ? (
              <FavoriteClaim />
            ) : (
              <FavoriteClaimLoginModal />
            )
          }
        </SoyuzExperiment>
        <MediaQuery minWidth={MQ_TABLET_MIN_WIDTH}>
          <CookiesBanner />
        </MediaQuery>
      </SahContainer>
    );
  }
}

export default withClientConfig()(SearchPage);
